'use client';

import * as React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PDFLetterEditor } from '@/components/pdf-editor';
import { Save, X, FileText, Loader2, Mail, RotateCcw } from 'lucide-react';
import { apiGet, apiPut, isSuccessResponse } from '@/lib/utils/api-helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getLawFirmLetterheadUrl } from '@/lib/utils/letterhead';
import { correspondenceKeys, accountKeys } from '@/lib/query-keys';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { regenerateLetterAction } from '@/lib/services/correspondences';
import { AUTOFILL_OPT_OUT } from '@/lib/constants/autofill-opt-out';

interface CorrespondenceData {
  id: number;
  correspondenceLetterBody?: string;
  subject?: string;
  fragments?: {
    mainContent?: string;
    quotedReplies?: unknown[];
  };
  emailto?: string;
  institution?: {
    id: number;
    name: string;
  };
  matter?: {
    id: number;
  };
  status: string;
}

export interface LetterEditorContentProps {
  institutionName: string;
  institutionId?: number;
  matterId: number;
  correspondenceId?: number;
  letterContent?: string;
  emailSubject?: string;
  emailBody?: string;
  onSave?: (content: string, emailSubject?: string, emailBody?: string) => void;
  onClose?: () => void;
  lawFirmName?: string;
  lawFirmAddress?: string;
  lawFirmLetterheadUrl?: string;
  matterReference?: string;
  deceasedName?: string;
  dateOfDeath?: string;
}

export function LetterEditorContent({
  institutionName,
  matterId,
  correspondenceId,
  letterContent,
  emailSubject,
  emailBody,
  onSave,
  onClose,
  matterReference,
  deceasedName,
  dateOfDeath
}: LetterEditorContentProps) {
  const [userContent, setUserContent] = React.useState<string | null>(null);
  const [userSubject, setUserSubject] = React.useState<string | null>(null);
  const [userBody, setUserBody] = React.useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'letter' | 'email'>('letter');
  const [userPdfUrl, setUserPdfUrl] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showResetConfirm, setShowResetConfirm] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);
  const queryClient = useQueryClient();

  const { data: lawFirmProfile } = useQuery<{ letterHeadFileId?: number }>({
    queryKey: accountKeys.lawFirmProfile,
    queryFn: async () => {
      const response = await apiGet('/api/proxy/account/profile');
      return response.data as { letterHeadFileId?: number };
    },
    staleTime: 5 * 60 * 1000
  });

  const { data: correspondence, isLoading: isLoadingCorrespondence } =
    useQuery<CorrespondenceData | null>({
      queryKey: correspondenceId
        ? correspondenceKeys.item(matterId, correspondenceId)
        : ['correspondence', 'disabled'],
      queryFn: async () => {
        if (!correspondenceId) return null;
        const response = await apiGet(
          `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`
        );
        return response.data as CorrespondenceData;
      },
      enabled: !!correspondenceId
    });

  const content =
    userContent ??
    correspondence?.correspondenceLetterBody ??
    letterContent ??
    '';
  const subject = userSubject ?? correspondence?.subject ?? emailSubject ?? '';
  const body =
    userBody ?? correspondence?.fragments?.mainContent ?? emailBody ?? '';
  const setContent = (v: string) => setUserContent(v);
  const setSubject = (v: string) => setUserSubject(v);
  const setBody = (v: string) => setUserBody(v);
  const setPdfUrl = (v: string | null) => setUserPdfUrl(v);

  const hasChanges = React.useMemo(() => {
    if (!correspondence) return false;
    return (
      content !== (correspondence.correspondenceLetterBody || '') ||
      subject !== (correspondence.subject || '') ||
      body !== (correspondence.fragments?.mainContent || '')
    );
  }, [content, subject, body, correspondence]);

  const {
    data: letterPreview,
    isLoading: isLoadingPreview,
    refetch: refetchPreview
  } = useQuery({
    queryKey: correspondenceId
      ? correspondenceKeys.letterPreview(matterId, correspondenceId)
      : ['letter-preview', 'disabled'],
    queryFn: async () => {
      if (!correspondenceId) return null;
      const response = await fetch(
        `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}/preview-letter?t=${Date.now()}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to load letter preview');
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!correspondenceId && viewMode === 'letter',
    staleTime: 0,
    gcTime: 0
  });

  // effectivePdfUrl falls back to the React Query–managed letterPreview unless the
  // user has explicitly nulled it via setUserPdfUrl(null) inside handleOpenPDF.
  const effectivePdfUrl = userPdfUrl ?? letterPreview ?? null;

  React.useEffect(() => {
    return () => {
      if (effectivePdfUrl && effectivePdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(effectivePdfUrl);
      }
    };
  }, [effectivePdfUrl]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (correspondenceId && matterId) {
        const updates: Record<string, unknown> = {};
        if (subject !== correspondence?.subject) {
          updates.subject = subject;
        }
        if (body !== correspondence?.fragments?.mainContent) {
          updates.mainContent = body;
        }
        if (content !== correspondence?.correspondenceLetterBody) {
          updates.correspondenceLetterBody = content;
        }
        if (Object.keys(updates).length > 0) {
          const response = await apiPut(
            `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`,
            updates
          );
          if (isSuccessResponse(response)) {
            toast.success('Correspondence saved', {
              description: 'Your changes have been saved successfully.'
            });
            if (correspondenceId) {
              queryClient.invalidateQueries({
                queryKey: correspondenceKeys.item(matterId, correspondenceId)
              });
            }
            queryClient.invalidateQueries({
              queryKey: correspondenceKeys.workflowData(
                matterId,
                'notification'
              )
            });
          }
        }
      }
      onSave?.(content, subject, body);
      onClose?.();
    } catch (error) {
      console.error('Failed to save correspondence:', error);
      toast.error('Save failed', {
        description: 'Failed to save your changes. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(letterContent || '');
    setSubject(emailSubject || '');
    setBody(emailBody || '');
    setPdfUrl(null);
    onClose?.();
  };

  const handleOpenPDF = async () => {
    if (viewMode === 'letter' && correspondenceId) {
      setIsPreviewLoading(true);
      try {
        if (hasChanges && correspondenceId && matterId) {
          const updates: Record<string, unknown> = {};
          if (subject !== correspondence?.subject) {
            updates.subject = subject;
          }
          if (body !== correspondence?.fragments?.mainContent) {
            updates.mainContent = body;
          }
          if (content !== correspondence?.correspondenceLetterBody) {
            updates.correspondenceLetterBody = content;
          }
          if (Object.keys(updates).length > 0) {
            const response = await apiPut(
              `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}`,
              updates
            );
            if (isSuccessResponse(response)) {
              if (correspondenceId) {
                await queryClient.invalidateQueries({
                  queryKey: correspondenceKeys.item(matterId, correspondenceId)
                });
              }
              await queryClient.invalidateQueries({
                queryKey: correspondenceKeys.workflowData(
                  matterId,
                  'notification'
                )
              });
            }
          }
        }
        if (effectivePdfUrl && effectivePdfUrl.startsWith('blob:')) {
          URL.revokeObjectURL(effectivePdfUrl);
          setPdfUrl(null);
        }
        if (correspondenceId) {
          await queryClient.invalidateQueries({
            queryKey: correspondenceKeys.letterPreview(
              matterId,
              correspondenceId
            )
          });
        }
        const result = await refetchPreview();
        setTimeout(() => {
          if (result?.data) {
            window.open(result.data, '_blank');
          }
        }, 100);
      } catch (error) {
        console.error('Failed to generate PDF preview:', error);
        toast.error('Failed to generate PDF preview');
      } finally {
        setIsPreviewLoading(false);
      }
    }
  };

  const handleConfirmReset = async () => {
    if (!correspondenceId) return;
    setIsResetting(true);
    try {
      const result = await regenerateLetterAction(matterId, correspondenceId);
      if (result.success) {
        const itemKey = correspondenceKeys.item(matterId, correspondenceId);
        await queryClient.invalidateQueries({ queryKey: itemKey });
        await queryClient.refetchQueries({ queryKey: itemKey });
        const data = queryClient.getQueryData<CorrespondenceData>(itemKey);
        if (data?.correspondenceLetterBody !== undefined) {
          setContent(data.correspondenceLetterBody || '');
        }
        if (data?.subject !== undefined) setSubject(data.subject || '');
        if (data?.fragments?.mainContent !== undefined)
          setBody(data.fragments.mainContent || '');
      } else {
        toast.error(result.error || 'Failed to reset letter');
      }
    } catch (_e) {
      toast.error('Failed to reset letter');
    } finally {
      setIsResetting(false);
      setShowResetConfirm(false);
    }
  };

  const getLetterType = () => {
    const name = institutionName.toLowerCase();
    if (
      name.includes('bank') ||
      name.includes('nab') ||
      name.includes('commonwealth') ||
      name.includes('westpac') ||
      name.includes('anz')
    ) {
      return 'bank';
    } else if (
      name.includes('insurance') ||
      name.includes('allianz') ||
      name.includes('suncorp')
    ) {
      return 'insurance';
    } else if (
      name.includes('government') ||
      name.includes('centrelink') ||
      name.includes('ato')
    ) {
      return 'government';
    }
    return 'general';
  };

  const isLoading =
    isLoadingCorrespondence || (viewMode === 'letter' && isLoadingPreview);

  return (
    <>
      <div className="flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Correspondence - {institutionName}
          </h2>
          <div className="flex items-center gap-3">
            <Label
              htmlFor="view-mode"
              className="text-muted-foreground text-sm"
            >
              View:
            </Label>
            <div className="flex items-center gap-2">
              <FileText className="text-muted-foreground h-4 w-4" />
              <Switch
                id="view-mode"
                checked={viewMode === 'email'}
                onCheckedChange={(checked) =>
                  setViewMode(checked ? 'email' : 'letter')
                }
              />
              <Mail className="text-muted-foreground h-4 w-4" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          </div>
        ) : viewMode === 'letter' ? (
          <div className="h-full">
            {correspondenceId && (
              <div className="bg-muted/50 mb-4 flex items-center justify-between rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <FileText className="text-muted-foreground h-4 w-4" />
                  <span className="text-muted-foreground text-sm">
                    {isPreviewLoading
                      ? 'Saving changes and generating PDF preview...'
                      : hasChanges
                        ? 'Click to save changes and view PDF in a new tab.'
                        : effectivePdfUrl
                          ? 'PDF preview ready. Click to regenerate and view in a new tab.'
                          : 'Click to generate and view PDF preview.'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenPDF}
                    disabled={isPreviewLoading}
                  >
                    {isPreviewLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Open PDF
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowResetConfirm(true)}
                    disabled={!correspondenceId || isResetting}
                    title="Reset letter to template"
                  >
                    {isResetting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Reset Letter
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
            <PDFLetterEditor
              value={content}
              onChange={setContent}
              letterType={getLetterType()}
              matterReference={matterReference}
              deceasedName={deceasedName}
              dateOfDeath={dateOfDeath}
              letterheadUrl={getLawFirmLetterheadUrl(
                lawFirmProfile?.letterHeadFileId
              )}
              hideBorder={true}
              hideShadow={true}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="email-subject" className="text-sm font-medium">
                Subject
              </Label>
              <input
                id="email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                {...AUTOFILL_OPT_OUT}
                className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring/70 mt-1 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={`Letter - ${deceasedName}`}
              />
            </div>
            <div>
              <Label htmlFor="email-body" className="text-sm font-medium">
                Email Body
              </Label>
              <textarea
                id="email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                {...AUTOFILL_OPT_OUT}
                className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring/70 mt-1 min-h-[400px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder="Enter email body..."
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 border-t pt-4">
        <div className="flex gap-3">
          <Button
            size="xs"
            density="compact"
            gap="standard"
            variant="outline"
            onClick={handleCancel}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset letter to template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard your edits and regenerate the letter content
              from the default template. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReset}
              disabled={isResetting}
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting
                </>
              ) : (
                'Confirm Reset'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// =============================================================================
// Dialog wrapper — preserves existing API for all current consumers
// =============================================================================

interface LetterEditorDialogProps extends LetterEditorContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LetterEditorDialog({
  open,
  onOpenChange,
  ...contentProps
}: LetterEditorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-5xl flex-col p-6">
        <LetterEditorContent
          {...contentProps}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
